import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { EstateWorkItem_FieldsFragment, useDocument_DownloadFilesMutation } from 'queries';

import InstructionsModal from 'pages/estate-vault/components/instructions-modal';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: '13px',
  },
  title: {
    fontWeight: 'medium',
  },
  actionButton: {
    borderRadius: '100px',
  },
  actionButtons: {
    display: 'flex',
    gap: '20px',
  },
  generateButton: {
    display: 'flex',
    marginTop: '20px',
    borderRadius: '100px',
    color: '#000000',
    backgroundColor: '#E53DE8',
  },
  helpText: {
    fontSize: '14px',
    color: '#596674',
  },
  headerIcons: {
    marginRight: '8px',
  },
  outlined: {
    borderRadius: '100px',
    borderColor: '#1C2541',
    color: '#1C2541',
  },
  downloadButton: {
    borderRadius: '100px',
    color: '#000000',
    backgroundColor: '#E53DE8',
    borderColor: '#E53DE8',
  },
}));

type AssignedTaskDocumentGenerationDetailsProps = {
  estateWorkItem: EstateWorkItem_FieldsFragment;
};

const AssignedTaskDocumentGenerationDetails = ({
  estateWorkItem,
}: AssignedTaskDocumentGenerationDetailsProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  const [downloadFile] = useDocument_DownloadFilesMutation({
    onCompleted: ({ data: downloadFilesData }) => {
      window.open(downloadFilesData[0].signedUrl, '_blank');
    },
    onError: (e) => {
      showAlert({
        message: 'Error downloading files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const onViewDocument = () => {
    history.push(`/home/${portalId}/task/${estateWorkItem.id}/template-document`);
  };

  const onEditDocument = () => {
    if (!estateWorkItem.documentGenerationTemplateId) {
      history.replace(`/home/${portalId}/task/${estateWorkItem.id}/template-questionnaire`);
      return;
    }
    history.push(`/home/${portalId}/task/${estateWorkItem.id}/template-data-wizard`);
  };

  const onDownloadDocument = () => {
    if (estateWorkItem.generatedDocumentId) {
      downloadFile({
        variables: {
          portalId,
          ids: [estateWorkItem.generatedDocumentId],
        },
      });
    }
  };

  const onViewInstructions = () => {
    setShowInstructionsModal(true);
  };

  return (
    <div className={classes.container}>
      {estateWorkItem.generatedDocumentId ? (
        <Typography className={classes.title}>
          Review and edit documents by clicking the buttons below
        </Typography>
      ) : (
        <>
          <Typography className={classes.title}>Get started on generating the document</Typography>
          <Typography className={classes.helpText}>
            Please note that document generation may take a few minutes to complete.
          </Typography>
        </>
      )}
      <div className={classes.actionButtons}>
        {estateWorkItem.generatedDocumentId ? (
          <>
            <Button
              variant="contained"
              color="primary"
              className={classes.actionButton}
              type="button"
              onClick={onEditDocument}
            >
              Edit document
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.actionButton}
              type="button"
              onClick={onViewDocument}
            >
              <ContentCopyIcon className={classes.headerIcons} fontSize="small" /> View document
            </Button>

            <Button
              variant="outlined"
              color="primary"
              className={classes.downloadButton}
              type="button"
              onClick={onDownloadDocument}
            >
              <DownloadIcon className={classes.headerIcons} fontSize="small" /> Download
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className={classes.outlined}
              type="button"
              onClick={onViewInstructions}
              disabled={!estateWorkItem.documentGenerationTemplate}
            >
              <MarkChatUnreadOutlinedIcon className={classes.headerIcons} fontSize="small" />
              Instructions
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            color="primary"
            className={classes.generateButton}
            type="button"
            onClick={onEditDocument}
          >
            <ContentCopyIcon className={classes.headerIcons} fontSize="small" /> Click to generate
            document
          </Button>
        )}
      </div>
      {showInstructionsModal && (
        <InstructionsModal
          showModal={showInstructionsModal}
          setShowModal={() => {
            setShowInstructionsModal(false);
          }}
          documentGenerationTemplate={estateWorkItem.documentGenerationTemplate || undefined}
        />
      )}
    </div>
  );
};

export default AssignedTaskDocumentGenerationDetails;
